import { designTokens } from "@itandi/itandi-bb-ui";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormRow } from "@itandi/itandi-bb-ui/FormRow";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputRadio } from "@itandi/itandi-bb-ui/InputRadio";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import React from "react";
import { useFormContext } from "react-hook-form";
import { OTHER_CONSTRUCTION_ITEM_ID } from "~/constants/DealConstructionConst";
import type { DealSettlementEdit } from "~/models/DealSettlement";
import { buildDefaultSettlement } from "~/models/DealSettlement";
import type { SettlementItem } from "~/models/SettlementItem";

type Props = {
	settlementItems: SettlementItem[];
};

export const SettlementForm: React.FC<Props> = ({ settlementItems }) => {
	const { register, formState, getValues, reset, setValue, watch } =
		useFormContext<DealSettlementEdit>();
	const { errors } = formState;
	const settlementItem = watch("settlementItem");
	const transactionType = watch("transactionType");
	const totalAmount = watch("totalAmount");
	const adjustTotalAmount = (
		transaction: "billing" | "refund",
		amount: number
	): number =>
		transaction === "billing" ? Math.abs(amount) : -Math.abs(amount);
	React.useEffect(() => {
		setValue("unitAmount", Number(totalAmount));
	}, [setValue, totalAmount]);
	const onChangeItem = React.useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>): void => {
			if (settlementItems !== null) {
				const selectItem =
					settlementItems.find((i) => i.id === Number(e.target.value)) ?? null;
				const { billingType, transactionType: currentTransactionType } =
					getValues();
				reset(
					{
						...buildDefaultSettlement(billingType),
						settlementItem: selectItem,
						transactionType: currentTransactionType
					},
					{ keepDefaultValues: true }
				);
			}
		},
		[getValues, reset, settlementItems]
	);
	const handleChangeTransactionType = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			const { value } = e.target;
			if (value === "billing" || value === "refund") {
				setValue("transactionType", value);
				setValue("totalAmount", adjustTotalAmount(value, Number(totalAmount)));
			}
		},
		[setValue, totalAmount]
	);
	return (
		<>
			<FormControl
				chip="必須"
				isInvalid={!!errors.settlementItem}
				label="精算項目"
			>
				<InputSelect
					onChange={onChangeItem}
					value={settlementItem?.id ?? -1}
					w="100%"
				>
					<option key={-1} value={-1}>
						精算項目を選択
					</option>
					{settlementItems.map(
						(item: SettlementItem): React.ReactNode => (
							<option key={item.id} value={item.id}>
								{item.name}
							</option>
						)
					)}
				</InputSelect>
				<FormControlText error={errors.settlementItem?.message} />
			</FormControl>
			{settlementItem?.id === OTHER_CONSTRUCTION_ITEM_ID && (
				<FormControl
					chip="必須"
					isInvalid={!!errors.otherName}
					label="精算項目名"
				>
					<Input placeholder="キャンペーン値引き" {...register("otherName")} />
					<FormControlText error={errors.otherName?.message} />
				</FormControl>
			)}
			<FormControl
				chip="必須"
				isInvalid={!!errors.transactionType}
				label="取引区分"
			>
				<Flex gap={designTokens["spacing/m"]}>
					<InputRadio
						checked={transactionType === "refund"}
						label="返還"
						onChange={handleChangeTransactionType}
						value="refund"
					/>
					<InputRadio
						checked={transactionType === "billing"}
						label="請求"
						onChange={handleChangeTransactionType}
						value="billing"
					/>
				</Flex>
				<FormControlText
					error={errors.transactionType?.message}
					help="「請求」を選択すると金額はプラス値に、「返還」を選択するとマイナス値として扱われます。"
				/>
			</FormControl>
			<FormRow>
				<FormControl chip="必須" isInvalid={!!errors.totalAmount} label="金額">
					<Input
						name="totalAmount"
						onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
							const { value } = e.target;
							if (Number(value) < 0) return;
							if (value === "") {
								setValue("totalAmount", value === "" ? "" : Number(value));
								return;
							}
							setValue(
								"totalAmount",
								adjustTotalAmount(transactionType, Number(value))
							);
						}}
						placeholder="金額"
						type="number"
						value={totalAmount === "" ? "" : Math.abs(totalAmount)}
					/>
					<FormControlText error={errors.totalAmount?.message} />
				</FormControl>
				<FormControl chip="必須" isInvalid={!!errors.taxType} label="税率">
					<InputSelect w="100%" {...register("taxType")}>
						<option value="nontaxable">0%</option>
						<option value="standard">10%</option>
					</InputSelect>
					<FormControlText error={errors.taxType?.message} />
				</FormControl>
			</FormRow>
			<FormControl isInvalid={!!errors.note} label="備考">
				<InputText
					maxRows={8}
					placeholder="備考）その他連絡事項など"
					resize="autoRows"
					rows={2}
					{...register("note")}
				/>
			</FormControl>
		</>
	);
};
